/**
 * Platform usage (owner, 1 Oct 2026): how the platform is being used across every firm and direct
 * client. Counts and dates only — the server's platform_usage_summary() never returns a name or a
 * fact-find, so this is safe for firms advising under their own AFSL too.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getPlatformUsage, explainAdminError, type PlatformUsage } from '../../services/adminApi.ts';
import { PROFESSION_LABELS, type Profession } from '../../services/tenantApi.ts';
import { AdminMessage, SectionLabel, StatusDot } from './AdminShell.tsx';

const MODULE_LABELS: Record<string, string> = {
  aged_care: 'Aged care',
  support_at_home: 'Support at Home',
};

const fmtMonth = (ym: string): string => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
};

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const n = (v: number): string => v.toLocaleString('en-AU');

/** One headline figure: the number large, what it counts beneath, the breakdown below that. */
const Figure = ({ label, value, detail }: { label: string; value: number; detail: string[] }) => (
  <div className="min-w-0 py-1">
    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</div>
    <div className="mt-1.5 text-4xl font-semibold tabular-nums tracking-tight text-slate-900">{n(value)}</div>
    <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
      {detail.map((d) => (
        <li key={d}>{d}</li>
      ))}
    </ul>
  </div>
);

const UsagePanel = () => {
  const [usage, setUsage] = useState<PlatformUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getPlatformUsage()
      .then((u) => { if (live) setUsage(u); })
      .catch((err) => { if (live) setError(explainAdminError(err, 'Could not load usage.')); });
    return () => { live = false; };
  }, []);

  if (error) return <AdminMessage tone="error">{error}</AdminMessage>;
  if (!usage) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
        <Loader2 size={18} className="animate-spin" /> Loading usage…
      </div>
    );
  }

  const maxMonth = Math.max(1, ...usage.soasByMonth.map((m) => m.generated));
  const modules = Object.entries(usage.savedScenariosByModule);
  const moduleTotal = modules.reduce((sum, [, v]) => sum + v, 0) || 1;

  return (
    <div className="space-y-10">
      <section className="grid grid-cols-2 gap-x-8 gap-y-6 border-b border-slate-200 pb-8 md:grid-cols-3 xl:grid-cols-5">
        <Figure
          label="Firms"
          value={usage.firms.total}
          detail={[
            `${n(usage.firms.ownLicence)} on their own AFSL`,
            `${n(usage.firms.underOurLicence)} under ours`,
            ...(usage.firms.notActive ? [`${n(usage.firms.notActive)} not active`] : []),
          ]}
        />
        <Figure label="Advisers" value={usage.advisers} detail={['Owners and advisers']} />
        <Figure
          label="Client files"
          value={usage.clientFiles.total}
          detail={[
            `${n(usage.clientFiles.viaFirms)} via firms · ${n(usage.clientFiles.direct)} direct`,
            `${n(usage.clientFiles.factFindComplete)} fact-finds complete`,
            `${n(usage.clientFiles.new30d)} new in 30 days`,
          ]}
        />
        <Figure
          label="Statements of Advice"
          value={usage.soas.total}
          detail={[`${n(usage.soas.published)} published`, `${n(usage.soas.ourLicence)} under our licence`, `${n(usage.soas.new30d)} in 30 days`]}
        />
        <Figure
          label="Accounts"
          value={usage.accounts.total}
          detail={[`${n(usage.accounts.active30d)} active in 30 days`, `${n(usage.accounts.active90d)} in 90 days`, `${n(usage.accounts.new30d)} new in 30 days`]}
        />
      </section>

      <section className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <SectionLabel>Statements of Advice, last 12 months</SectionLabel>
          {usage.soasByMonth.length === 0 ? (
            <p className="text-sm text-slate-500">None yet.</p>
          ) : (
            <>
              <div className="flex h-44 items-end gap-2 border-b border-slate-200" role="img" aria-label="Statements of Advice generated and published by month">
                {usage.soasByMonth.map((m) => (
                  <div key={m.month} className="flex h-full flex-1 flex-col justify-end" title={`${fmtMonth(m.month)}: ${m.generated} generated, ${m.published} published`}>
                    <div className="mb-1 text-center text-[11px] font-semibold tabular-nums text-slate-600">{m.generated}</div>
                    <div className="relative w-full rounded-t-md bg-[#1a6fd4]" style={{ height: `${(m.generated / maxMonth) * 100}%`, minHeight: 2 }}>
                      <div className="absolute bottom-0 w-full rounded-t-md bg-[#0b1a33]" style={{ height: `${m.generated ? (m.published / m.generated) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-1.5 flex gap-2">
                {usage.soasByMonth.map((m) => (
                  <div key={m.month} className="flex-1 text-center text-[11px] text-slate-500">{fmtMonth(m.month)}</div>
                ))}
              </div>
              <div className="mt-3 flex gap-5 text-xs text-slate-600">
                <StatusDot color="#1a6fd4" label="Generated" />
                <StatusDot color="#0b1a33" label="Published" />
              </div>
            </>
          )}
        </div>

        <div>
          <SectionLabel>Saved scenarios by module</SectionLabel>
          {modules.length === 0 ? (
            <p className="text-sm text-slate-500">None saved yet.</p>
          ) : (
            <ul className="space-y-4">
              {modules.map(([m, v]) => (
                <li key={m}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-700">{MODULE_LABELS[m] ?? m}</span>
                    <span className="font-semibold tabular-nums text-slate-900">{n(v)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                    <div className="h-1.5 rounded-full bg-[#17b8e0]" style={{ width: `${(v / moduleTotal) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs leading-relaxed text-slate-500">Only aged care and Support at Home scenarios are saved to the server today.</p>
        </div>
      </section>

      <section>
        <SectionLabel>By firm</SectionLabel>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-[0.08em] text-slate-500">
                <th className="px-5 py-3 font-semibold">Firm</th>
                <th className="px-3 py-3 text-right font-semibold">Advisers</th>
                <th className="px-3 py-3 text-right font-semibold">Client files</th>
                <th className="px-3 py-3 text-right font-semibold">Fact-finds complete</th>
                <th className="px-3 py-3 text-right font-semibold">SOAs</th>
                <th className="px-3 py-3 text-right font-semibold">Published</th>
                <th className="px-5 py-3 font-semibold">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usage.byFirm.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-sm text-slate-500">No partner firms yet.</td>
                </tr>
              )}
              {usage.byFirm.map((f) => (
                <tr key={f.id} className="transition hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{f.name}</div>
                    <div className="text-xs text-slate-500">
                      {f.profession ? PROFESSION_LABELS[f.profession as Profession] ?? f.profession : 'Firm'}
                      {f.status !== 'active' ? ` · ${f.status}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-700">{f.advisers}</td>
                  <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-900">{f.clientFiles}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-700">{f.factFindsComplete}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-700">{f.soasGenerated}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-slate-700">{f.soasPublished}</td>
                  <td className="px-5 py-3 text-slate-500">{fmtDate(f.lastActivity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-center text-xs text-slate-400">
        Counts and dates only — no client names or details. Updated {new Date(usage.generatedAt).toLocaleString('en-AU')}.
      </p>
    </div>
  );
};

export default UsagePanel;
